import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Text } from "@core/index";
import {
  useGetPublicAssignment,
  generatePresignedUrlOnly,
} from "@packages/api/madlenAPI";
import { configureAxios } from "../../../axiosConfig";

import swannLogo from "@packages/assets/swann-icon.png";

type PublicItem =
  | {
      id: string | number;
      text: string;
      ttsUrl?: string;
      image_url?: string;
      imageUrl?: string;
      tts_alignment?: {
        characters: string[];
        character_start_times_seconds: number[];
        character_end_times_seconds: number[];
      };
    }
  | string;

function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()!.split(";").shift() || null;
  return null;
}

function setCookie(name: string, value: string, days = 180) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${d.toUTCString()}`;
  document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/`;
}

async function convertBlobToWav16kMono16bit(inputBlob: Blob): Promise<Blob> {
  const TARGET_SAMPLE_RATE = 16000;
  const TARGET_CHANNELS = 1;

  const arrayBuffer = await inputBlob.arrayBuffer();
  const AudioCtx =
    (window as any).AudioContext || (window as any).webkitAudioContext;
  const decodeCtx = new AudioCtx();
  const decodedBuffer: AudioBuffer = await new Promise(
    (resolve: (v: AudioBuffer) => void, reject: (e: any) => void) => {
      decodeCtx.decodeAudioData(
        arrayBuffer.slice(0),
        (buf: AudioBuffer) => resolve(buf),
        (err: any) => reject(err),
      );
    },
  );
  await decodeCtx.close();

  const duration = decodedBuffer.duration;
  const length = Math.ceil(duration * TARGET_SAMPLE_RATE);
  const offlineCtx = new (window as any).OfflineAudioContext(
    TARGET_CHANNELS,
    length,
    TARGET_SAMPLE_RATE,
  );
  const source = offlineCtx.createBufferSource();
  source.buffer = decodedBuffer;
  source.connect(offlineCtx.destination);
  source.start(0);
  const rendered: AudioBuffer = await offlineCtx.startRendering();
  const channelData = rendered.getChannelData(0);

  const bytesPerSample = 2;
  const blockAlign = TARGET_CHANNELS * bytesPerSample;
  const byteRate = TARGET_SAMPLE_RATE * blockAlign;
  const dataSize = channelData.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  function writeString(offset: number, str: string) {
    for (let i = 0; i < str.length; i += 1) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  }

  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, "WAVE");

  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, TARGET_CHANNELS, true);
  view.setUint32(24, TARGET_SAMPLE_RATE, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);

  writeString(36, "data");
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < channelData.length; i += 1) {
    let s = channelData[i];
    if (s < -1) s = -1;
    if (s > 1) s = 1;
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  return new Blob([view], { type: "audio/wav" });
}

// base64 conversion no longer needed; using S3 presigned upload

function chooseSupportedMimeType(): string {
  const candidates = ["audio/ogg;codecs=opus", "audio/webm"];
  for (const c of candidates) {
    // @ts-ignore
    if (
      typeof MediaRecorder !== "undefined" &&
      (MediaRecorder as any).isTypeSupported &&
      (MediaRecorder as any).isTypeSupported(c)
    ) {
      return c;
    }
  }
  return "";
}

const PublicPronunciationPageV2 = () => {
  const { t } = useTranslation();
  const params = useParams();
  const code = params.code as string | undefined;

  const [solverName, setSolverName] = useState<string>("");
  const [showNameStep, setShowNameStep] = useState<boolean>(true);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [sending, setSending] = useState<boolean>(false);
  const [latestResult, setLatestResult] = useState<any | null>(null);
  const [showWords, setShowWords] = useState<boolean>(false);
  const [resultsById, setResultsById] = useState<Record<string, any>>({});
  const [animKey, setAnimKey] = useState<string>("item-0");

  // MediaRecorder state
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<BlobPart[]>([]);
  const [recording, setRecording] = useState<boolean>(false);
  const [elapsedMs, setElapsedMs] = useState<number>(0);
  const timerRef = useRef<number | null>(null);
  const mimeTypeRef = useRef<string>("audio/webm;codecs=opus");
  // We auto-send on stop; no need to keep blob in state
  const autoSendOnStopRef = useRef<boolean>(false);

  // TTS playback state
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioLoading, setAudioLoading] = useState<boolean>(false);
  const [audioPlaying, setAudioPlaying] = useState<boolean>(false);

  // Slow TTS playback state
  const slowAudioRef = useRef<HTMLAudioElement | null>(null);
  const [slowAudioLoading, setSlowAudioLoading] = useState<boolean>(false);
  const [slowAudioPlaying, setSlowAudioPlaying] = useState<boolean>(false);

  // Segment playback (single-word) at 0.65x
  const segmentAudioRef = useRef<HTMLAudioElement | null>(null);
  const [segmentPlayingKey, setSegmentPlayingKey] = useState<string | null>(
    null,
  );
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);
  const WORD_MARGIN_S = 0;

  // Sound effects for results
  const correctSoundRef = useRef<HTMLAudioElement | null>(null);
  const wrongSoundRef = useRef<HTMLAudioElement | null>(null);

  function cleanupAudio() {
    try {
      const a = audioRef.current;
      if (a) {
        a.pause();
        a.src = "";
        a.load();
      }
    } catch {}
    audioRef.current = null;
    setAudioLoading(false);
    setAudioPlaying(false);

    // Also cleanup slow audio
    try {
      const slowA = slowAudioRef.current;
      if (slowA) {
        slowA.pause();
        slowA.src = "";
        slowA.load();
      }
    } catch {}
    slowAudioRef.current = null;
    setSlowAudioLoading(false);
    setSlowAudioPlaying(false);

    // Cleanup segment audio
    try {
      const seg = segmentAudioRef.current;
      if (seg) {
        seg.pause();
        seg.src = "";
        seg.load();
      }
    } catch {}
    segmentAudioRef.current = null;
    setSegmentPlayingKey(null);
    setHighlightedKey(null);
  }

  function playResultSound(score: number) {
    try {
      const isGoodScore = score >= 80;
      const soundRef = isGoodScore ? correctSoundRef : wrongSoundRef;

      if (!soundRef.current) {
        soundRef.current = new Audio();
        soundRef.current.src = isGoodScore
          ? "/sounds/correct.mp3"
          : "/sounds/wrong.mp3";
        soundRef.current.volume = 0.3;
      }

      soundRef.current.currentTime = 0;
      void soundRef.current.play();
    } catch (error) {
      // Silently fail if sound can't be played
      console.warn("Could not play result sound:", error);
    }
  }

  async function playTTS(url?: string) {
    if (!url) return;
    try {
      setAudioLoading(true);
      // Stop other audios
      try {
        const slowA = slowAudioRef.current;
        if (slowA) slowA.pause();
      } catch {}
      setSlowAudioPlaying(false);
      setSlowAudioLoading(false);
      try {
        segmentAudioRef.current?.pause();
      } catch {}
      setSegmentPlayingKey(null);
      let audio = audioRef.current;
      if (!audio) {
        audio = new Audio();
        audioRef.current = audio;
        try {
          audio.crossOrigin = "anonymous";
        } catch {}
      }
      if (audio.src !== url) {
        audio.src = url;
      }
      // Ensure restart from beginning if ended
      try {
        if (
          (audio as any).ended ||
          (audio.duration && audio.currentTime >= audio.duration - 0.05)
        ) {
          audio.currentTime = 0;
        }
      } catch {}
      audio.volume = 1;
      audio.playbackRate = 1;
      audio.preload = "auto";
      audio.onloadeddata = () => setAudioLoading(false);
      audio.oncanplaythrough = () => setAudioLoading(false);
      audio.onplaying = () => setAudioLoading(false);
      audio.onplay = () => setAudioPlaying(true);
      audio.onpause = () => {
        setAudioPlaying(false);
        setHighlightedKey(null);
        setAudioLoading(false);
      };
      audio.onended = () => {
        setAudioPlaying(false);
        setHighlightedKey(null);
        setAudioLoading(false);
      };
      audio.onwaiting = () => setAudioLoading(true);
      audio.onerror = () => {
        setAudioLoading(false);
        setAudioPlaying(false);
        setHighlightedKey(null);
      };
      audio.ontimeupdate = () => {
        try {
          const t = audio?.currentTime ?? 0;
          updateHighlightFromTime(t);
        } catch {}
      };

      await audio.play().catch(() => {
        setAudioLoading(false);
      });
    } catch {
      setAudioLoading(false);
    }
  }

  async function playSlowTTS(url?: string) {
    if (!url) return;
    try {
      setSlowAudioLoading(true);
      // Stop other audios
      try {
        const a = audioRef.current;
        if (a) a.pause();
      } catch {}
      setAudioPlaying(false);
      setAudioLoading(false);
      try {
        segmentAudioRef.current?.pause();
      } catch {}
      setSegmentPlayingKey(null);
      let audio = slowAudioRef.current;
      if (!audio) {
        audio = new Audio();
        slowAudioRef.current = audio;
        try {
          audio.crossOrigin = "anonymous";
        } catch {}
      }
      if (audio.src !== url) {
        audio.src = url;
      }
      // Ensure restart from beginning if ended
      try {
        if (
          (audio as any).ended ||
          (audio.duration && audio.currentTime >= audio.duration - 0.05)
        ) {
          audio.currentTime = 0;
        }
      } catch {}
      audio.volume = 1;
      audio.playbackRate = 0.6; // Slow playback
      audio.preload = "auto";
      audio.onloadeddata = () => setSlowAudioLoading(false);
      audio.oncanplaythrough = () => setSlowAudioLoading(false);
      audio.onplaying = () => setSlowAudioLoading(false);
      audio.onplay = () => setSlowAudioPlaying(true);
      audio.onpause = () => {
        setSlowAudioPlaying(false);
        setHighlightedKey(null);
        setSlowAudioLoading(false);
      };
      audio.onended = () => {
        setSlowAudioPlaying(false);
        setHighlightedKey(null);
        setSlowAudioLoading(false);
      };
      audio.onwaiting = () => setSlowAudioLoading(true);
      audio.onerror = () => {
        setSlowAudioLoading(false);
        setSlowAudioPlaying(false);
        setHighlightedKey(null);
      };
      audio.ontimeupdate = () => {
        try {
          const t = audio?.currentTime ?? 0;
          updateHighlightFromTime(t);
        } catch {}
      };

      await audio.play().catch(() => {
        setSlowAudioLoading(false);
      });
    } catch {
      setSlowAudioLoading(false);
    }
  }

  function togglePlay() {
    const item = items[currentIndex];
    if (!item?.ttsUrl) return;
    const a = audioRef.current;
    if (a && a.src === item.ttsUrl && !a.paused) {
      a.pause();
      setHighlightedKey(null);
      setAudioLoading(false);
      return;
    }
    void playTTS(item.ttsUrl);
  }

  function toggleSlowPlay() {
    const item = items[currentIndex];
    if (!item?.ttsUrl) return;
    const slowA = slowAudioRef.current;
    if (slowA && slowA.src === item.ttsUrl && !slowA.paused) {
      slowA.pause();
      setHighlightedKey(null);
      setSlowAudioLoading(false);
      return;
    }
    void playSlowTTS(item.ttsUrl);
  }

  useEffect(() => {
    if (!code) return;
    const saved = getCookie(`pron_solver_${code}`);
    if (saved) {
      setSolverName(decodeURIComponent(saved));
      setShowNameStep(false);
    }
  }, [code]);

  useEffect(() => {
    if (!code) return;
    if (solverName && solverName.trim().length > 0) {
      setCookie(`pron_solver_${code}`, solverName.trim());
    }
  }, [code, solverName]);

  const {
    data: publicAssignment,
    isLoading,
    isError,
  } = useGetPublicAssignment(code || "", {
    query: { enabled: !!code, refetchInterval: 10000 },
  });

  const items: {
    id: string;
    text: string;
    ttsUrl?: string;
    imageUrl?: string;
    ttsAlignment?: {
      characters: string[];
      character_start_times_seconds: number[];
      character_end_times_seconds: number[];
    };
  }[] = useMemo(() => {
    const raw: any = (publicAssignment as any)?.items;
    if (!raw || !Array.isArray(raw)) return [];
    return raw
      .map((it: PublicItem, idx: number) => {
        if (typeof it === "string")
          return { id: String(idx + 1), text: it, ttsUrl: undefined };
        if (it && typeof (it as any).text === "string" && (it as any).id)
          return {
            id: String((it as any).id),
            text: (it as any).text,
            ttsUrl: (it as any).ttsUrl,
            imageUrl: (it as any).imageUrl || (it as any).image_url,
            ttsAlignment: (it as any).tts_alignment,
          };
        if (it && typeof (it as any).text === "string")
          return {
            id: String(idx + 1),
            text: (it as any).text,
            ttsUrl: (it as any).ttsUrl,
            imageUrl: (it as any).imageUrl || (it as any).image_url,
            ttsAlignment: (it as any).tts_alignment,
          };
        return null;
      })
      .filter(Boolean) as {
      id: string;
      text: string;
      ttsUrl?: string;
      imageUrl?: string;
      ttsAlignment?: {
        characters: string[];
        character_start_times_seconds: number[];
        character_end_times_seconds: number[];
      };
    }[];
  }, [publicAssignment]);

  const totalCount = items.length;
  const completedCount = useMemo(
    () => Object.keys(resultsById).filter((k) => !!resultsById[k]).length,
    [resultsById],
  );

  useEffect(() => {
    const keyPart = latestResult ? "res" : "item";
    setAnimKey(`${keyPart}-${currentIndex}`);
  }, [currentIndex, latestResult]);

  // Auto play TTS when a new sentence appears and the name step is done.
  useEffect(() => {
    const currentItemForEffect = items[currentIndex];
    if (!showNameStep && !latestResult && currentItemForEffect?.ttsUrl) {
      void playTTS(currentItemForEffect.ttsUrl);
    }
    if (latestResult) {
      try {
        audioRef.current?.pause();
        slowAudioRef.current?.pause();
      } catch {}
    }
    return () => {
      cleanupAudio();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, latestResult, showNameStep]);

  function goToIndex(next: number) {
    if (recording) return; // don't allow while recording
    const clamped = Math.max(0, Math.min(totalCount, next));
    setCurrentIndex(clamped);
    const nextItem = items[clamped];
    if (nextItem && resultsById[nextItem.id]) {
      setLatestResult(resultsById[nextItem.id]);
    } else {
      setLatestResult(null);
    }
  }

  // Timer
  function startTimer() {
    const startedAt = Date.now();
    setElapsedMs(0);
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, 100) as unknown as number;
  }
  function stopTimer() {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }

  async function startRecording() {
    try {
      // Stop TTS while starting a new recording
      try {
        audioRef.current?.pause();
        slowAudioRef.current?.pause();
      } catch {}
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = chooseSupportedMimeType();
      mimeTypeRef.current = mime || "audio/webm";
      const recorder = new MediaRecorder(
        stream,
        mime ? { mimeType: mime } : undefined,
      );

      recordedChunksRef.current = [];
      setRecording(true);
      setElapsedMs(0);
      autoSendOnStopRef.current = false;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stopTimer();
        const finalBlob = new Blob(recordedChunksRef.current, {
          type: mimeTypeRef.current,
        });
        setRecording(false);
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        if (autoSendOnStopRef.current) {
          // Fire-and-forget; internal state shows loading
          void sendBlob(finalBlob);
        }
      };
      recorderRef.current = recorder;
      recorder.start(250);
      startTimer();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Microphone permission error", err);
      setRecording(false);
    }
  }

  function cancelRecording() {
    try {
      if (recorderRef.current && recorderRef.current.state === "recording") {
        recorderRef.current.stop();
      }
    } catch {}
    stopTimer();
    setRecording(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }

  function stopRecordingAndKeep() {
    try {
      if (recorderRef.current && recorderRef.current.state === "recording") {
        autoSendOnStopRef.current = true;
        recorderRef.current.stop();
      }
    } catch {}
  }

  async function sendBlob(blob: Blob) {
    if (!code || !solverName || solverName.trim().length === 0) return;
    const item = items[currentIndex];
    if (!item) return;

    setSending(true);
    try {
      // Convert to WAV 16k mono 16-bit and upload to S3 via presigned URL
      let wavBlob: Blob;
      try {
        wavBlob = await convertBlobToWav16kMono16bit(blob);
      } catch {
        // Fallback: upload original blob if conversion fails
        wavBlob = blob;
      }

      const safeFileName = `pron-${code}-${item.id}-${Date.now()}.wav`;
      const presigned = await generatePresignedUrlOnly({
        fileName: safeFileName,
      });
      const signedUrl = presigned?.presignedUrl;
      if (!signedUrl) throw new Error("No presigned URL returned");

      await fetch(signedUrl, { method: "PUT", body: wavBlob });
      const fileUrl = signedUrl.split("?")[0];

      const res = await configureAxios<any>({
        url: `/swann-pronunciation/public/${code}/submit-url`,
        method: "POST",
        data: {
          solverName: solverName.trim(),
          itemId: item.id,
          fileUrl,
        },
      });

      // Response may be a map or a single submission
      let resultPayload: any | null = null;
      if (res && typeof res === "object" && res.results) {
        const byId = (res.results as Record<string, any[]>)[item.id] || [];
        resultPayload = byId.length ? byId[byId.length - 1]?.result : null;
      } else if (res && typeof res === "object") {
        resultPayload = (res as any).result ?? null;
      }
      setLatestResult(resultPayload || null);
      if (resultPayload) {
        setResultsById((prev) => ({ ...prev, [item.id]: resultPayload }));
        // Play result sound based on overall score
        const overallScore = Number(resultPayload.overall || 0);
        playResultSound(overallScore);
      }
      setShowWords(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Submission failed", e);
    } finally {
      setSending(false);
    }
  }

  // Move hook-dependent computations above any early returns to keep hooks order stable
  const atLast = currentIndex >= Math.max(0, items.length - 1);
  const currentItem = items[currentIndex];

  // Compute current segments for highlight calculation
  const currentSegments = useMemo(() => {
    return getWordSegments(currentItem?.text || "", currentItem?.ttsAlignment);
  }, [currentItem?.text, currentItem?.ttsAlignment]);

  if (!code) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Text variant="h2">{t("pronunciationPublic.error")}</Text>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Text variant="p">{t("pronunciationPublic.loading")}</Text>
      </div>
    );
  }

  if (isError || !publicAssignment) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-lg w-full bg-white dark:bg-dark-600 rounded-2xl shadow-lg border border-gray-200 dark:border-dark-400 p-8">
          <Text variant="h2" className="mb-2">
            {t("pronunciationPublic.error")}
          </Text>
          <Text variant="p">{t("pronunciationPublic.errorNotFound")}</Text>
        </div>
      </div>
    );
  }

  // Helper function to determine if the current item is a single word
  const isSingleWord = (text: string): boolean => {
    if (!text) return false;
    // Remove punctuation and trim, then check if it's a single word
    const cleanText = text.replace(/[^\w\s]/g, "").trim();
    return cleanText.split(/\s+/).length === 1;
  };

  function colorClassForScore(score?: number): string {
    const s =
      typeof score === "number" ? Math.max(0, Math.min(100, score)) : -1;
    // 80-100: green, 60-80: yellow, 40-60: orange, 0-40: red
    if (s >= 80)
      return "bg-emerald-200 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"; // yeşil
    if (s >= 60)
      return "bg-yellow-200 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"; // sarı
    if (s >= 40)
      return "bg-orange-200 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300"; // turuncu
    return "bg-red-200 text-red-800 dark:bg-red-900/20 dark:text-red-300"; // kırmızı
  }

  function renderColoredSentence(
    originalText: string,
    wordResults: any[],
  ): JSX.Element {
    if (
      !originalText ||
      !Array.isArray(wordResults) ||
      wordResults.length === 0
    ) {
      return <span>{originalText}</span>;
    }

    type Range = { start: number; end: number; className: string };
    const ranges: Range[] = [];

    // Prefer precise character ranges from API word_parts
    for (const wr of wordResults) {
      const score =
        typeof wr?.scores?.overall === "number"
          ? wr.scores.overall
          : typeof wr?.scores?.pronunciation === "number"
            ? wr.scores.pronunciation
            : undefined;
      const className = colorClassForScore(score);

      if (Array.isArray(wr?.word_parts) && wr.word_parts.length > 0) {
        for (const part of wr.word_parts) {
          const begin = Number(part?.beginIndex);
          const endInc = Number(part?.endIndex);
          const charType = Number(part?.charType);
          if (
            Number.isFinite(begin) &&
            Number.isFinite(endInc) &&
            begin >= 0 &&
            endInc >= begin &&
            begin < originalText.length
          ) {
            // Only color alphanumeric parts; skip pure punctuation segments (charType === 1)
            if (charType === 1) continue;
            const end = Math.min(originalText.length, endInc + 1);
            ranges.push({ start: begin, end, className });
          }
        }
      }
    }

    // If we couldn't build any range via word_parts, fallback to a broader token-based approach
    if (ranges.length === 0) {
      // Tokenize into words, numbers, dates, percentages, times, etc., plus whitespace/punct
      const tokens = originalText.match(
        /\s+|[A-Za-zÀ-ÖØ-öø-ÿ0-9%:./\-]+|[^A-Za-zÀ-ÖØ-öø-ÿ0-9\s]+/g,
      ) || [originalText];
      let cursor = 0;
      let wrIdx = 0;
      const parts: JSX.Element[] = [];
      tokens.forEach((tk, idx) => {
        const start = cursor;
        const end = start + tk.length;
        cursor = end;
        const isToken = /[A-Za-zÀ-ÖØ-öø-ÿ0-9%:./\-]+/.test(tk);
        if (isToken && wrIdx < wordResults.length) {
          const wr = wordResults[wrIdx++];
          const s =
            typeof wr?.scores?.overall === "number"
              ? wr.scores.overall
              : typeof wr?.scores?.pronunciation === "number"
                ? wr.scores.pronunciation
                : undefined;
          const cls = colorClassForScore(s);
          parts.push(
            <span
              key={`c-${idx}`}
              className={`px-1.5 py-0.5 rounded-md ${cls} mx-0.5 inline-block`}
            >
              {tk}
            </span>,
          );
        } else {
          parts.push(<span key={`p-${idx}`}>{tk}</span>);
        }
      });
      return <span className="inline">{parts}</span>;
    }

    // Normalize and sort ranges; avoid overlaps
    ranges.sort((a, b) => a.start - b.start || a.end - b.end);
    const normalized: Range[] = [];
    for (const r of ranges) {
      const prev = normalized[normalized.length - 1];
      if (!prev) {
        normalized.push(r);
      } else if (r.start >= prev.end) {
        normalized.push(r);
      } else if (r.end > prev.end) {
        // Trim overlapping start to previous end
        normalized.push({
          start: prev.end,
          end: r.end,
          className: r.className,
        });
      }
      // If r is fully inside prev, skip
    }

    // Build final pieces interleaving plain text
    const out: JSX.Element[] = [];
    let pos = 0;
    normalized.forEach((r, i) => {
      if (pos < r.start) {
        out.push(
          <span key={`t-${i}-p`}>{originalText.slice(pos, r.start)}</span>,
        );
      }
      const slice = originalText.slice(r.start, r.end);
      out.push(
        <span
          key={`t-${i}-c`}
          className={`px-1.5 py-0.5 rounded-md ${r.className} mx-0.5 inline-block`}
        >
          {slice}
        </span>,
      );
      pos = r.end;
    });
    if (pos < originalText.length) {
      out.push(<span key="t-last">{originalText.slice(pos)}</span>);
    }

    return <span className="inline">{out}</span>;
  }

  function renderClickableSentence(
    originalText: string,
    alignment:
      | {
          characters: string[];
          character_start_times_seconds: number[];
          character_end_times_seconds: number[];
        }
      | undefined,
    url?: string,
  ): JSX.Element {
    const segments = getWordSegments(originalText, alignment);
    const parts = segments.map((seg, idx) => {
      const key = `${idx}-${seg.start}-${seg.end}`;
      if (
        seg.isWord &&
        url &&
        seg.start !== undefined &&
        seg.end !== undefined
      ) {
        const active =
          highlightedKey === key ||
          segmentPlayingKey === (key || `${seg.start}-${seg.end}`);
        return (
          <button
            key={key}
            type="button"
            onClick={() => void playSegment(url, seg.start, seg.end, key)}
            className={`inline px-1.5 py-0.5 mx-0 rounded-md transition-colors ${
              active
                ? "bg-cyan-200 text-cyan-900 dark:bg-cyan-900/40 dark:text-cyan-200"
                : "hover:bg-gray-100 dark:hover:bg-dark-500"
            }`}
            aria-label=""
          >
            {seg.token}
          </button>
        );
      }
      // Non-word or missing alignment: render as plain text
      return (
        <span key={key} className="inline">
          {seg.token}
        </span>
      );
    });
    return (
      <span className="inline text-3xl sm:text-4xl font-semibold tracking-tight">
        {parts}
      </span>
    );
  }

  // Build word tokens and their time ranges from character-level alignment.
  // We split by spaces while preserving punctuation tokens as individual items.
  function getWordSegments(
    text: string,
    alignment?: {
      characters: string[];
      character_start_times_seconds: number[];
      character_end_times_seconds: number[];
    },
  ): { token: string; start?: number; end?: number; isWord: boolean }[] {
    if (!text) return [];
    const tokens = text.match(/\s+|[\p{L}\p{N}'-]+|[^\p{L}\p{N}\s]+/gu) || [
      text,
    ];
    if (
      !alignment ||
      !Array.isArray(alignment.characters) ||
      !Array.isArray(alignment.character_start_times_seconds) ||
      alignment.characters.length !==
        alignment.character_start_times_seconds.length
    ) {
      return tokens.map((tk) => ({
        token: tk,
        isWord: /[\p{L}\p{N}'-]/u.test(tk),
      }));
    }

    // Map each token to a range of character indices in the original text
    const ranges: {
      startIdx: number;
      endIdx: number;
      token: string;
      isWord: boolean;
    }[] = [];
    let cursor = 0;
    for (const tk of tokens) {
      const startIdx = cursor;
      const endIdx = startIdx + tk.length; // exclusive
      ranges.push({
        startIdx,
        endIdx,
        token: tk,
        isWord: /[\p{L}\p{N}'-]/u.test(tk),
      });
      cursor = endIdx;
    }

    // Walk the alignment characters across the original text to map char positions.
    // alignment.characters should equal originalText.split("") typically, including spaces and punctuation.
    // We'll compute token start/end times by taking min start and max end time of included characters.
    const out: {
      token: string;
      start?: number;
      end?: number;
      isWord: boolean;
    }[] = [];
    for (const r of ranges) {
      // Determine character indices overlapping this token
      const charStart = r.startIdx;
      const charEnd = r.endIdx - 1;
      let minStart: number | undefined;
      let maxEnd: number | undefined;
      for (
        let ci = charStart;
        ci <= charEnd && ci < alignment.characters.length;
        ci += 1
      ) {
        const st = alignment.character_start_times_seconds[ci];
        const en = alignment.character_end_times_seconds?.[ci] ?? st;
        if (typeof st === "number" && !Number.isNaN(st)) {
          minStart = minStart === undefined ? st : Math.min(minStart, st);
        }
        if (typeof en === "number" && !Number.isNaN(en)) {
          maxEnd = maxEnd === undefined ? en : Math.max(maxEnd, en);
        }
      }
      out.push({
        token: r.token,
        start: minStart,
        end: maxEnd,
        isWord: r.isWord,
      });
    }
    return out;
  }

  // Play only a segment of the item's TTS URL at 0.65x between start..end seconds
  const playSegment = async (
    url: string,
    start?: number,
    end?: number,
    key?: string,
  ) => {
    if (!url || start === undefined || end === undefined || !(end > start))
      return;
    try {
      // Pause other audios
      try {
        audioRef.current?.pause();
      } catch {}
      try {
        slowAudioRef.current?.pause();
      } catch {}
      let seg = segmentAudioRef.current;
      if (!seg) {
        seg = new Audio();
        segmentAudioRef.current = seg;
      }
      if (seg.src !== url) {
        seg.src = url;
      }
      seg.playbackRate = 0.5;
      const startAdj = Math.max(0, start - WORD_MARGIN_S);
      const endAdj = Math.max(startAdj + 0.02, end + WORD_MARGIN_S);
      seg.currentTime = startAdj;
      setSegmentPlayingKey(key || `${start}-${end}`);
      setHighlightedKey(key || `${start}-${end}`);
      // Guard to stop near target end; also onended cleanup key
      const clearGuards = () => {
        if ((seg as any)._guard) {
          window.clearInterval((seg as any)._guard);
          (seg as any)._guard = null;
        }
      };
      seg.onended = () => {
        clearGuards();
        setSegmentPlayingKey(null);
        setHighlightedKey(null);
      };
      seg.ontimeupdate = () => {
        setHighlightedKey(key || `${start}-${end}`);
      };
      clearGuards();
      (seg as any)._guard = window.setInterval(() => {
        if (!seg) return;
        if (seg.paused) return;
        if (seg.currentTime >= endAdj - 0.02) {
          try {
            seg.pause();
          } catch {}
          clearGuards();
          setSegmentPlayingKey(null);
          setHighlightedKey(null);
        }
      }, 16);
      await seg.play();
    } catch {}
  };

  function updateHighlightFromTime(timeSec: number) {
    if (!currentSegments || currentSegments.length === 0) {
      setHighlightedKey(null);
      return;
    }
    for (let i = 0; i < currentSegments.length; i += 1) {
      const seg = currentSegments[i];
      if (!seg.isWord || seg.start === undefined || seg.end === undefined)
        continue;
      if (timeSec >= seg.start && timeSec < seg.end) {
        const key = `${i}-${seg.start}-${seg.end}`;
        if (key !== highlightedKey) setHighlightedKey(key);
        return;
      }
    }
    if (highlightedKey !== null) setHighlightedKey(null);
  }

  return (
    <div className="min-h-screen bg-white dark:bg-dark-500 text-gray-900 dark:text-gray-100 flex flex-col">
      <div className="flex-1 max-w-3xl w-full mx-auto p-6 sm:p-8 flex flex-col">
        {/* Local styles for subtle animations */}
        <style>{`
          @keyframes fadeInUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes fadeInScale { from { opacity: 0; transform: scale(.98); } to { opacity: 1; transform: scale(1); } }
          .cloud-glow {
            filter:
              drop-shadow(0 12px 22px rgba(255, 255, 255, 0.85))
              drop-shadow(0 0 44px rgba(255, 255, 255, 0.6));
          }
          .dark .cloud-glow {
            filter:
              drop-shadow(0 12px 22px rgba(255, 255, 255, 0.55))
              drop-shadow(0 0 54px rgba(255, 255, 255, 0.45));
          }
        `}</style>
        {/* Name step overlay */}
        {showNameStep && (
          <div className="fixed inset-0 bg-white/90 dark:bg-black/70 backdrop-blur-sm z-20 flex items-center justify-center">
            <div className="w-full max-w-md p-8 rounded-3xl shadow-xl border border-gray-200 dark:border-dark-400 bg-white dark:bg-dark-600 text-center transform transition-all">
              <Text variant="h1" className="text-2xl font-semibold mb-6">
                {t("pronunciationPublicV2.whatsYourName")}
              </Text>
              <input
                autoFocus
                type="text"
                value={solverName}
                onChange={(e) => setSolverName(e.target.value)}
                placeholder={t("pronunciationPublic.enterName") || ""}
                className="w-full px-5 py-3 rounded-full border border-gray-300 dark:border-dark-400 bg-gray-50 dark:bg-dark-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent mb-4"
              />
              <button
                type="button"
                disabled={!solverName.trim()}
                onClick={() => setShowNameStep(false)}
                className="inline-flex items-center justify-center w-full px-6 py-3 rounded-full bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-medium shadow-sm hover:from-cyan-600 hover:to-cyan-700 disabled:opacity-50"
              >
                {t("common.continue")}
              </button>
            </div>
          </div>
        )}

        {/* Header + Progress */}
        <div className="mb-6">
          <div className="text-center">
            <Text variant="h1" className="text-2xl font-semibold">
              {publicAssignment?.title || t("pronunciationPublic.title")}
            </Text>
            {publicAssignment?.description && (
              <Text
                variant="p"
                className="text-gray-600 dark:text-gray-400 mt-1"
              >
                {publicAssignment.description}
              </Text>
            )}
          </div>

          {totalCount > 0 && (
            <div className="mt-4 flex items-center justify-center">
              <div className="text-xs opacity-70 font-medium">
                {currentIndex < totalCount
                  ? `${Math.min(currentIndex + 1, totalCount)}/${totalCount}`
                  : `${totalCount}/${totalCount}`}
              </div>
            </div>
          )}
        </div>

        {/* Content card */}
        <div className="flex-1 flex items-center justify-center">
          <div
            key={animKey}
            className="mx-auto rounded-3xl border border-gray-200 dark:border-dark-400 bg-white dark:bg-dark-600 p-6 sm:p-8 shadow-sm"
            style={{ animation: "fadeInScale 240ms ease both" }}
          >
            {/* Step: show sentence or result */}
            {!latestResult && currentItem && (
              <div
                className="text-center"
                style={{ animation: "fadeInUp 260ms ease both" }}
              >
                {currentItem?.imageUrl && (
                  <div className="flex justify-center mb-6">
                    <img
                      src={currentItem.imageUrl}
                      alt={currentItem.text}
                      className="max-h-56 w-auto object-cover rounded-3xl cloud-glow"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="mb-8 transition-all">
                  {renderClickableSentence(
                    currentItem.text,
                    currentItem.ttsAlignment,
                    currentItem.ttsUrl,
                  )}
                </div>

                {sending ? (
                  <div className="mt-8 flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-cyan-500 animate-pulse" />
                    <span>{t("common.pleaseWait")}</span>
                  </div>
                ) : !recording ? (
                  <div className="flex flex-col items-center gap-4">
                    <div className="flex items-center gap-2">
                      {currentItem?.ttsUrl && (
                        <button
                          type="button"
                          onClick={togglePlay}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-gray-300 dark:border-dark-400 hover:bg-gray-50 dark:hover:bg-dark-500"
                          title={
                            audioPlaying
                              ? (t("common.pause") as string)
                              : (t("common.play") as string)
                          }
                          aria-label={
                            audioPlaying
                              ? (t("common.pause") as string)
                              : (t("common.play") as string)
                          }
                        >
                          {audioLoading ? (
                            <span className="inline-block w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                          ) : audioPlaying ? (
                            // Pause icon
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              className="w-5 h-5"
                            >
                              <path d="M6.75 4.5A1.5 1.5 0 0 1 8.25 3h.5A1.5 1.5 0 0 1 10.25 4.5v15A1.5 1.5 0 0 1 8.75 21h-.5a1.5 1.5 0 0 1-1.5-1.5v-15ZM13.75 4.5A1.5 1.5 0 0 1 15.25 3h.5A1.5 1.5 0 0 1 17.25 4.5v15A1.5 1.5 0 0 1 15.75 21h-.5a1.5 1.5 0 0 1-1.5-1.5v-15Z" />
                            </svg>
                          ) : (
                            // Speaker icon
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              className="w-5 h-5"
                            >
                              <path d="M3 10v4a1 1 0 0 0 1 1h3l3.29 3.29A1 1 0 0 0 11 18V6a1 1 0 0 0-1.71-.71L7 8H4a1 1 0 0 0-1 1Zm13.54-6.64a1 1 0 0 0-1.08 1.64A8 8 0 0 1 18 12a8 8 0 0 1-2.54 5.99 1 1 0 0 0 1.36 1.46A10 10 0 0 0 20 12a10 10 0 0 0-3.18-7.19 1 1 0 0 0-.28-.45Zm-2.83 2.83a1 1 0 1 0-1.41 1.41A4 4 0 0 1 13 12a4 4 0 0 1-.7 2.24 1 1 0 1 0 1.64 1.13A6 6 0 0 0 15 12a6 6 0 0 0-1.29-3.81Z" />
                            </svg>
                          )}
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={!solverName}
                        onClick={startRecording}
                        className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 text-white shadow-lg hover:scale-105 active:scale-95 transition-transform"
                        title={t("pronunciationPublic.startRecording") || ""}
                        aria-label={
                          t("pronunciationPublic.startRecording") || ""
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="w-9 h-9"
                        >
                          <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3Zm-7-3a1 1 0 1 1 2 0 5 5 0 1 0 10 0 1 1 0 1 1 2 0 7 7 0 0 1-6 6.93V21a1 1 0 1 1-2 0v-3.07A7 7 0 0 1 5 11Z" />
                        </svg>
                      </button>
                      {currentItem?.ttsUrl && (
                        <button
                          type="button"
                          onClick={toggleSlowPlay}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-gray-300 dark:border-dark-400 hover:bg-gray-50 dark:hover:bg-dark-500"
                          title="Yavaş oynat (0.75x)"
                          aria-label="Yavaş oynat (0.75x)"
                        >
                          {slowAudioLoading ? (
                            <span className="inline-block w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                          ) : slowAudioPlaying ? (
                            // Pause icon with turtle color
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              className="w-5 h-5 text-black"
                            >
                              <path d="M6.75 4.5A1.5 1.5 0 0 1 8.25 3h.5A1.5 1.5 0 0 1 10.25 4.5v15A1.5 1.5 0 0 1 8.75 21h-.5a1.5 1.5 0 0 1-1.5-1.5v-15ZM13.75 4.5A1.5 1.5 0 0 1 15.25 3h.5A1.5 1.5 0 0 1 17.25 4.5v15A1.5 1.5 0 0 1 15.75 21h-.5a1.5 1.5 0 0 1-1.5-1.5v-15Z" />
                            </svg>
                          ) : (
                            // Turtle icon
                            <svg
                              fill="#000000"
                              height="20px"
                              width="20px"
                              version="1.2"
                              baseProfile="tiny"
                              id="_x31_"
                              xmlns="http://www.w3.org/2000/svg"
                              xmlnsXlink="http://www.w3.org/1999/xlink"
                              viewBox="0 0 256 175"
                              xmlSpace="preserve"
                            >
                              <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                              <g
                                id="SVGRepo_tracerCarrier"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              ></g>
                              <g id="SVGRepo_iconCarrier">
                                {" "}
                                <path d="M185,29.7v0.2l15-12.6c-11.4-6.1-23.9-9.5-37.5-9.5s-26.2,3.4-37.1,9.3l15,12.6L185,29.7L185,29.7z M105.1,93.2 c-24.7,0-45,20.3-45,45v4.5l0,0c0.2,9.5,7.9,17,17.4,17s17.2-7.5,17.4-17l0,0v-4.5c0-5.7,4.5-10.1,10.1-10.1 c9.7,0,17.6-7.9,17.6-17.6S114.8,93.2,105.1,93.2z M221.9,92.8c-9.7,0-17.6,7.9-17.6,17.6v32.4l0,0c0.4,9.3,7.9,16.8,17.4,16.8 s17-7.5,17.4-16.8l0,0v-32.7C239.3,100.5,231.4,92.8,221.9,92.8z M252.7,101.9c0-4.3-1.4-8.5-3.4-12c-2.2,1.6-4.7,2.6-7.7,2.6 c4.1,4.9,6.5,11,6.5,17.6v5.5C251.3,111.8,252.7,107,252.7,101.9z M131.7,110.6c0,5.7-1.8,10.7-4.7,15c11.6,2.6,23.5,4.1,35.7,4.1 c11.4,0,22.1-1.2,32.7-3.4v-16c0-6.5,2.4-12.6,6.3-17h-76.5C129,98,131.7,103.9,131.7,110.6z M125,93.4L125,93.4c0.2,0,0-0.2,0-0.2 V93.4z M245.8,79.6c0-2.4-2-4.7-4.5-4.7h-0.6c-3.4-21.7-15.4-40.6-32.7-52.5l-17.6,14.8c9.7,11.4,16.4,24.1,20.3,37.9h-9.5 c-4.3-13-11.4-25.6-21.5-35.9h-32.9c-10.1,10.5-17,22.7-21.5,35.9h-9.5c3.9-13.4,10.3-26,19.9-36.9l-18.5-15.6 c-17,12.2-29.4,30.8-32.7,52.5h-1.2c-2.4,0-4.5,2-4.5,4.5c0,2.4,2,4.5,4.5,4.5h157.8C243.8,84.1,245.8,82,245.8,79.6z M81.6,89.5 c-4.9-0.6-8.9-4.7-8.9-9.9v-8.9c0-18.7-15-33.9-33.9-33.9c-18.7,0-33.9,15-33.9,33.9v8.9c0,4.9,4.1,8.9,8.9,8.9h24.5 c2.2,11.4,8.5,20.7,17.6,27.4C61.1,104.3,70.2,95.2,81.6,89.5z M24.2,64.4c-3,0-5.7-2.4-5.7-5.7c0-3,2.4-5.7,5.7-5.7 s5.7,2.4,5.7,5.7C29.8,61.9,27.4,64.4,24.2,64.4z"></path>{" "}
                              </g>
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {t("pronunciationPublic.startRecording")}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="flex items-center justify-center gap-3">
                      <span className="inline-block w-3 h-3 rounded-full bg-rose-500 animate-pulse" />
                      <span className="font-mono text-sm">
                        {new Date(elapsedMs).toISOString().substring(14, 19)}
                      </span>
                    </div>
                    <div className="flex items-center justify-center gap-3">
                      <button
                        type="button"
                        onClick={cancelRecording}
                        className="px-5 py-2 rounded-full border border-gray-300 dark:border-dark-400 hover:bg-gray-50 dark:hover:bg-dark-500"
                      >
                        {t("common.cancel")}
                      </button>
                      <button
                        type="button"
                        onClick={stopRecordingAndKeep}
                        className="px-5 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-cyan-600 text-white hover:from-cyan-600 hover:to-cyan-700"
                      >
                        {t("pronunciationPublic.stopRecording")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Results */}
            {!!latestResult && (
              <div
                className="space-y-6"
                style={{ animation: "fadeInUp 260ms ease both" }}
              >
                <div className="text-center">
                  <div
                    className="mx-auto w-32 h-32 rounded-full flex items-center justify-center text-4xl font-bold text-white shadow"
                    style={{
                      backgroundImage: `conic-gradient(#10B981 ${Math.max(0, Math.min(100, Number(latestResult.overall || 0))) * 3.6}deg, #E5E7EB 0deg)`,
                    }}
                  >
                    <span className="relative">
                      <span className="absolute inset-0 rounded-full -z-10" />
                      <span className="text-emerald-700 dark:text-emerald-300 bg-white dark:bg-dark-600 rounded-full px-2 py-1 text-3xl">
                        {Math.round(Number(latestResult.overall || 0))}
                      </span>
                    </span>
                  </div>
                  <div className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                    {t("pronunciationPublic.overall")}
                  </div>
                  {/* Show colored sentence only for multi-word content */}
                  {currentItem?.text &&
                    !isSingleWord(currentItem.text) &&
                    Array.isArray(latestResult.words) &&
                    latestResult.words.length > 0 && (
                      <div className="mt-4 text-lg leading-8">
                        {renderColoredSentence(
                          currentItem.text,
                          latestResult.words,
                        )}
                      </div>
                    )}
                </div>

                {/* Show different score grids for single words vs sentences */}
                {currentItem?.text && isSingleWord(currentItem.text) ? (
                  // For single words, show the word repetition above scores
                  <div className="space-y-4">
                    {/* Word repetition display */}
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                        {currentItem.text}
                      </div>
                    </div>
                  </div>
                ) : (
                  // Full scores grid for sentences
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                      [
                        t("pronunciationPublic.pronunciation"),
                        latestResult.pronunciation,
                      ],
                      [t("pronunciationPublic.fluency"), latestResult.fluency],
                      [t("pronunciationPublic.rhythm"), latestResult.rhythm],
                      [t("pronunciationPublic.speed"), latestResult.speed],
                      [
                        t("pronunciationPublic.duration"),
                        latestResult.duration || latestResult.numeric_duration,
                      ],
                      [
                        t("pronunciationPublic.integrity"),
                        latestResult.integrity,
                      ],
                    ].map(([label, value], idx) => (
                      <div
                        key={idx}
                        className="px-3 py-2 rounded-xl bg-gray-50 dark:bg-dark-500 border border-gray-200 dark:border-dark-400 flex items-center justify-between"
                      >
                        <span className="text-xs opacity-70">
                          {label as string}
                        </span>
                        <span className="text-sm font-semibold">
                          {String(value ?? "-")}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Hide word details section for single words */}
                {currentItem?.text &&
                  !isSingleWord(currentItem.text) &&
                  Array.isArray(latestResult.words) &&
                  latestResult.words.length > 0 && (
                    <div className="border border-gray-200 dark:border-dark-400 rounded-2xl overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setShowWords((s) => !s)}
                        className="w-full px-4 py-3 text-left bg-gray-50 dark:bg-dark-500 hover:bg-gray-100 dark:hover:bg-dark-400 transition-colors"
                      >
                        <span className="text-sm font-medium">
                          {t("pronunciationPublic.words")} (
                          {latestResult.words.length})
                        </span>
                      </button>
                      {showWords && (
                        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {latestResult.words.map((w: any, wi: number) => (
                            <div
                              key={`${w.word}-${wi}`}
                              className="rounded-xl border border-gray-200 dark:border-dark-400 p-3"
                            >
                              <div className="flex items-center justify-between">
                                <div className="font-medium">{w.word}</div>
                                <div className="text-sm opacity-80">
                                  {t("pronunciationPublic.score")}:{" "}
                                  {w.scores?.overall ??
                                    w.scores?.pronunciation ??
                                    "-"}
                                </div>
                              </div>
                              {Array.isArray(w.phonemes) &&
                                w.phonemes.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {w.phonemes.map((p: any, pi: number) => (
                                      <span
                                        key={pi}
                                        className="px-2 py-0.5 rounded-md text-xs bg-gray-100 text-gray-800 dark:bg-dark-500 dark:text-gray-200"
                                      >
                                        /{p.phoneme}/{" "}
                                        {typeof p.pronunciation === "number"
                                          ? `(${p.pronunciation})`
                                          : ""}
                                      </span>
                                    ))}
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                <div className="flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setLatestResult(null);
                    }}
                    className="px-5 py-2 rounded-full border border-gray-300 dark:border-dark-400 hover:bg-gray-50 dark:hover:bg-dark-500"
                  >
                    {t("common.retry")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const nextIndex = currentIndex + 1;
                      setLatestResult(null);
                      goToIndex(nextIndex);
                    }}
                    className="px-5 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-cyan-600 text-white hover:from-cyan-600 hover:to-cyan-700"
                  >
                    {atLast ? t("common.finish") : t("common.continue")}
                  </button>
                </div>
              </div>
            )}

            {/* Completed state */}
            {!currentItem && !latestResult && (
              <div
                className="text-center py-10"
                style={{ animation: "fadeInUp 260ms ease both" }}
              >
                <Text variant="h2" className="text-2xl font-semibold mb-2">
                  {t("pronunciationPublicV2.completed")}
                </Text>
                {/* Overall average */}
                <div className="mt-6 flex items-center justify-center">
                  {(() => {
                    const values = Object.values(resultsById) as any[];
                    const numbers = values
                      .map((v) => Number((v as any)?.overall ?? 0))
                      .filter((n) => !Number.isNaN(n));
                    const avg = numbers.length
                      ? Math.round(
                          numbers.reduce((a, b) => a + b, 0) / numbers.length,
                        )
                      : 0;
                    return (
                      <div className="space-y-3">
                        <div
                          className="mx-auto w-36 h-36 rounded-full flex items-center justify-center text-5xl font-bold text-white shadow"
                          style={{
                            backgroundImage: `conic-gradient(#0ea5e9 ${avg * 3.6}deg, #E5E7EB 0deg)`,
                          }}
                        >
                          <span className="text-cyan-700 dark:text-cyan-300 bg-white dark:bg-dark-600 rounded-full px-3 py-1">
                            {avg}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                          {t("pronunciationPublic.overall")}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div className="mt-6 text-cyan-700 dark:text-cyan-300 font-medium">
                  🎉 {t("common.thankYou")}
                </div>
                <div className="mt-2 text-sm opacity-70">
                  {completedCount}/{totalCount}
                </div>
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={() => goToIndex(0)}
                    className="px-5 py-2 rounded-full border border-gray-300 dark:border-dark-400 hover:bg-gray-50 dark:hover:bg-dark-500"
                  >
                    {/* Restart browsing */}←
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="py-6 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
        <span className="inline-flex items-center gap-0">
          <img src={swannLogo} alt="Swann" className="w-8 h-8" />
          <span>{t("pronunciationPublicV2.createdBySwann")}</span>
        </span>
      </div>
    </div>
  );
};

export default PublicPronunciationPageV2;
